import React, { useEffect, useState } from 'react';

import { templateSchema } from '../../core/validations/survey_template';
import ClassificacaoQuestaoField from '../Catalog/ClassificacaoQuestaoField';
import CheckboxPropagacao from '../Catalog/CheckboxPropagacao';
import { mudouEstrutura } from '../../pages/surveys-template/resources';
import { salvarClassificacaoSeMudou } from '../../services/classificacao';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import Options from '../../pages/surveys-template/Options';
import GridOptions from '../Grid/GridOptions';
import GridPreview from '../Grid/GridPreview';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CustomRadioGroup,
  CustomTextarea,
  InputText,
 } from '../../components/Input';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  Heading,
  Stack,
  HStack,
  ModalHeader,
  Grid,
  Box,
  Text,
} from "@chakra-ui/react";


export default function EditQuestionModal({
  isOpen,
  onClose,
  question,
  index,
  edit
}) {
  const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
  const [optionsTotal, setOptionsTotal] = useState(0);
  const [isGridInitialized, setIsGridInitialized] = useState(false);
  // CA7.2: desmarcado por padrão — propagar sobrescreve questionários já
  // respondidos e precisa ser um ato deliberado a cada edição.
  const [propagar, setPropagar] = useState(false);
  const [erroClassificacao, setErroClassificacao] = useState(null);

  const {
    register,
    control,
    setValue,
    getValues,
    reset,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: { form: [question] },
    resolver: yupResolver(templateSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (isOpen && question) {
      Object.keys(question).forEach((key) => {
        setValue(key, question[key]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, question, setValue]);

  const { fields } = useFieldArray({
    control,
    name: 'form',
  });

  const questionType = useWatch({
    control,
    name: `form.${fields?.length - 1}.type`,
  });

  const certificationPrerequisite = useWatch({
    control,
    name: `form.${fields?.length - 1}.isCertified`,
  });

  const hasHelper =
    useWatch({
    control,
    name: `form.${fields?.length - 1}.hasHelper`,
    }) || 'Não';

  const watchedGridRows = useWatch({
    control,
    name: `form.${fields?.length - 1}.gridRows`,
  });

  const watchedGridColumns = useWatch({
    control,
    name: `form.${fields?.length - 1}.gridColumns`,
  });


  const handleSave = async () => {
    const valores = getValues().form[0];

    // EG-41 — CA04/CA07: a classificação grava AGORA, por id, e não junto com
    // o botão Salvar do template. O save do template versiona e recria as
    // questões com ids novos: se a classificação fosse por lá, a propagação
    // ficaria sem alvo. Falha aqui não pode engolir a edição do resto.
    try {
      await salvarClassificacaoSeMudou({
        original: question,
        valores,
        propagar,
      });
      setErroClassificacao(null);
    } catch (falha) {
      setErroClassificacao(
        falha?.response?.data?.message ||
          'Não foi possível salvar a classificação desta questão.'
      );
      return;
    }

    // EG-41: a comparação é feita AQUI, e não no formulário-pai.
    //
    // Aqui os dois lados têm o mesmo formato: `question` foi o que semeou o
    // defaultValues deste form, então diferença encontrada é diferença real. No
    // pai o mesmo diff mentia, porque lá a questão carrega campos derivados
    // (description, o id do useFieldArray) que o modal não devolve.
    //
    // dirtyFields não serve como sinal: pilar e tema entram por setValue, sem
    // register, e o RHF não os registra em dirtyFields — ele vinha vazio mesmo
    // depois de o gestor trocar o tema.
    const somenteClassificacao = !mudouEstrutura(valores, question);

    edit(valores, index, { somenteClassificacao });
    onClose();
  };

  const {
    fields: options,
    remove: removeOptions,
  } = useFieldArray({
    control,
    name: `form.${fields?.length - 1}.options`,
  });

  const {
    fields: gridRows,
    append: appendGridRow,
    remove: removeGridRow,
  } = useFieldArray({
    control,
    name: `form.${fields?.length - 1}.gridRows`,
  });

  const {
    fields: gridColumns,
    append: appendGridColumn,
    remove: removeGridColumn,
  } = useFieldArray({
    control,
    name: `form.${fields?.length - 1}.gridColumns`,
  });

  const handlenumberOfAlternativesChange = () => {
    const count = parseInt(question.options ? question.options.length : 0);
    setOptionsTotal(count);
  };

  const handleAddGridRow = () => {
    appendGridRow({
      description: '',
      row_order: gridRows.length,
    });
  };

  const handleRemoveGridRow = (rowIndex) => {
    removeGridRow(rowIndex);
  };

  const handleAddGridColumn = () => {
    appendGridColumn({
      description: '',
    });
  };

  const handleRemoveGridColumn = (colIndex) => {
    removeGridColumn(colIndex);
  };

  useEffect(() => {
    if (optionsTotal === 0 && (questionType === 'multiple_choice' || questionType === 'multi_choice') && question.options.length > 0) {
      handlenumberOfAlternativesChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionType, question])

  useEffect(() => {
    if (questionType === 'multiple_choice') {
    setIsCheckboxSelected(true);
    } else {
    setIsCheckboxSelected(false);
    }
  }, [questionType]);

  useEffect(() => {
    if (questionType === 'grid' && !isGridInitialized && gridRows.length === 0) {
      setIsGridInitialized(true);
      // Inicializar com 3 linhas
      appendGridRow({ description: '', row_order: 0 });
      appendGridRow({ description: '', row_order: 1 });
      appendGridRow({ description: '', row_order: 2 });
      
      // Inicializar com 4 colunas
      appendGridColumn({ description: '' });
      appendGridColumn({ description: '' });
      appendGridColumn({ description: '' });
      appendGridColumn({ description: '' });
    } else if (questionType !== 'grid') {
      setIsGridInitialized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionType]);

  useEffect(() => {
    if (
    (questionType === 'text' ||
      questionType === 'percentage' ||
      questionType === 'number' ||
      questionType === 'money' ||
      questionType === 'grid') &&
    options?.length
    ) {
    setOptionsTotal(0);
    removeOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionType, options?.length]);

  const handleCancel = () => {
    reset();
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent
        maxW="1329px"
        minH="361px"
        p="32px 60px"
        borderRadius="18px"
        overflow="hidden"
      >
        <ModalHeader minH="32px" p="0">
          <Heading fontSize={["18px", "20px"]} lineHeight="normal">
            Editar pergunta
          </Heading>
        </ModalHeader>

        <ModalBody p="32px 0">
        {fields?.map(
           (field, index) =>
            index === fields?.length - 1 && (
             <Stack spacing='26px' key={field.id}>
              <Grid
               templateColumns={
                isCheckboxSelected ? '1fr 8fr 1fr' : '2fr 8fr'
               }
               gap={6}
              >
               <InputText
                {...register(`form.${index}.code`)}
                label='Código da pergunta'
                placeholder='Código da pergunta'
                labelSize='sm'
                error={errors['form']?.[index]?.code}
               />

               <InputText
                {...register(`form.${index}.title`)}
                label='Título da pergunta'
                placeholder='Digite o título da pergunta'
                labelSize='sm'
                error={errors['form']?.[index]?.title}
                expandable
               />

               {isCheckboxSelected && (
                <CustomRadioGroup
                 register={register}
                 label='Com pontuação?'
                 fieldName={`form.${index}.isCertified`}
                 defaultValue={question.isCertified}
                />
               )}
              </Grid>

              {(questionType === 'multi_choice' || questionType === 'multiple_choice') &&
              optionsTotal !== 0 ? (
               <Options
                index={index}
                register={register}
                errors={errors}
                options={options}
                isCheckboxSelected={isCheckboxSelected}
                certificationPrerequisite={certificationPrerequisite}
               />
              ) : null}

              {questionType === 'grid' && (
                <>
                  <GridOptions
                    index={index}
                    register={register}
                    errors={errors}
                    gridRows={gridRows}
                    gridColumns={gridColumns}
                    onAddRow={handleAddGridRow}
                    onRemoveRow={handleRemoveGridRow}
                    onAddColumn={handleAddGridColumn}
                    onRemoveColumn={handleRemoveGridColumn}
                  />
                  <Box>
                    <Text fontWeight={600} fontSize='16px' mb='4'>
                      Pré-visualização
                    </Text>
                    <GridPreview gridRows={watchedGridRows || gridRows} gridColumns={watchedGridColumns || gridColumns} />
                  </Box>
                </>
              )}

              <HStack spacing={6} justify='flex-start' flexWrap='wrap'>
               <CustomRadioGroup
                register={register}
                label='Pergunta de anos anteriores?'
                fieldName={`form.${index}.previousYears`}
                defaultValue={question.previousYears}
               />
               <CustomRadioGroup
                register={register}
                label='Pergunta obrigatória?'
                fieldName={`form.${index}.isRequired`}
                defaultValue={question.isRequired}
               />
               <CustomRadioGroup
                register={register}
                label='Evidências em arquivos?'
                fieldName={`form.${index}.requestFiles`}
                defaultValue={question.requestFiles}
               />

               <CustomRadioGroup
                register={register}
                label='Evidências em fotos/imagens?'
                fieldName={`form.${index}.requestImages`}
                defaultValue={question.requestImages}
               />
               <CustomRadioGroup
                register={register}
                label='Possui campo de ajuda/helper?'
                fieldName={`form.${index}.hasHelper`}
                defaultValue={question.hasHelper}
               />

               {/* EG-41 — CA04: os mesmos dropdowns do formulário de criação,
                   agora no lápis do card. Mesmo componente e mesmo schema: a
                   regra do CA03 não pode divergir entre as duas telas. */}
               {/* EG-41 — CA07: o checkbox de propagação pertence À SEÇÃO de
                   classificação, não à fileira de flags da pergunta. Solto, ele
                   parecia uma opção da questão como qualquer outra, quando na
                   verdade decide se a mudança alcança questionários já
                   respondidos. */}
               <ClassificacaoQuestaoField
                control={control}
                setValue={setValue}
                prefixo={`form.${index}`}
                erro={errors?.form?.[index]?.id_theme?.message || erroClassificacao}
               >
                <CheckboxPropagacao
                 size='sm'
                 isChecked={propagar}
                 onChange={setPropagar}
                />
               </ClassificacaoQuestaoField>
               <CustomRadioGroup
                register={register}
                label='Possui SOMA do período?'
                fieldName={`form.${index}.hasSum`}
                defaultValue={question.hasSum}
                hasTooltip
                tooltipLabel='Sinalize com SIM, caso deseje que o input faça uma SOMA de todos os registros do período. Usar somente em casos de inputs alfanuméricos.'
                isDisabled={
                 questionType === 'text' ||
                 questionType === 'multi_choice' ||
                 questionType === 'multiple_choice'
                }
               />
               <CustomRadioGroup
                register={register}
                label='Possui MÉDIA do período?'
                fieldName={`form.${index}.hasAverage`}
                defaultValue={question.hasAverage}
                hasTooltip
                tooltipLabel='Sinalize com SIM, caso deseje que o input faça uma MÉDIA de todos os registros do período. Usar somente em casos de inputs alfanuméricos.'
                isDisabled={
                 questionType === 'text' ||
                 questionType === 'multi_choice' ||
                 questionType === 'multiple_choice'
                }
               />
              </HStack>

              <Stack spacing={6}>
               <CustomTextarea
                {...register(`form.${index}.helper.description`)}
                label='Texto de ajuda/helper'
                placeholder='Se necessário forneça uma descrição para a pergunta'
                h='111px'
                isDisabled={hasHelper === 'Não'}
               />
               <InputText
                {...register(`form.${index}.helper.url_video`)}
                label='URL de ajuda/helper'
                placeholder='Informe a URL do video'
                labelSize='sm'
                isDisabled={hasHelper === 'Não'}
               />
              </Stack>
             </Stack>
            )
          )}          
        </ModalBody>

        <ModalFooter p={0} gap={5}>
          <Button variant="secondary" size={'lg'} onClick={handleCancel}>
            Fechar
          </Button>
          <Button variant="primary" size={'lg'} isDisabled={!isDirty} onClick={handleSave}>
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
